import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { themeColor } from '../Theming';
import {
  ANALYTICS_PAGE_NAMES,
  trackClickedActionEvent,
} from '../Common/Analytics/utils';
import { usePageViewTracking } from '../Common/Hooks';
import env from '../env';
import ActivateNow from './components/ActivateNow';
import { onClose } from '../Common/KindredAndroidInterface/KindredAndroidInterface';
import { useNavigate } from 'react-router-dom';
import { Path } from '../Routing/Paths';

const Container = styled.main`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-height: 100dvh;
  overflow-x: hidden;
  margin: 0 auto;
  background: #ffffff;
`;

const AccessibilityHeader = styled.header`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: clamp(560px, 75dvh, 640px);
  overflow: hidden;
  box-sizing: border-box;
  padding: clamp(56px, 10dvh, 80px) 0 clamp(56px, 10dvh, 80px);

  &::before {
    position: absolute;
    left: 50%;
    bottom: 0;
    width: clamp(900px, 220vw, 1200px);
    aspect-ratio: 1;
    border-radius: 50%;
    background: #f5f6f8;
    transform: translateX(-50%);
    content: '';
  }

  @media (max-width: 390px) {
    min-height: clamp(430px, calc(100dvh - 130px), 500px);
    padding: 32px 0;
  }

  @media (min-width: 600px) {
    padding-inline: 24px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    min-height: 460px;
    padding-block: 32px;
  }
`;

const AccessibilityTitle = styled.h1`
  position: relative;
  z-index: 1;
  width: calc(100% - 40px);
  max-width: 420px;
  margin: 0 0 20px;
  color: #1b2457;
  font-family: 'Fira Sans', sans-serif;
  font-size: 35px;
  font-weight: 700;
  font-style: normal;
  line-height: 115%;
  letter-spacing: 0;
  text-align: center;

  @media (max-width: 390px) {
    font-size: 30px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    font-size: 28px;
  }
`;

const BottomContent = styled.section`
  width: 100%;
  box-sizing: border-box;
  flex-direction: column;
  padding-bottom: max(5px, env(safe-area-inset-bottom));
`;

const DisclaimerContainer = styled.div`
  background-color: ${themeColor('white')};
  width: calc(100% - 40px);
  max-width: 540px;
  box-sizing: border-box;
  margin: 0 auto;
  border-radius: 10px;
  padding: 20px;
  overflow: auto;
  height: clamp(180px, 30dvh, 260px);
  flex-grow: 1;
  font-family: 'Fira Sans', sans-serif;
  color: #1b2457;
  font-size: 15px;
  font-weight: 400;
  font-style: normal;
  line-height: 21px;
  letter-spacing: 0;
  position: relative;
  z-index: 1;

  @media (max-width: 390px) {
    width: calc(100% - 28px);
    height: clamp(105px, 20dvh, 145px);
    padding: 12px;
    font-size: 14px;
    line-height: 19px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: clamp(150px, 34dvh, 180px);
  }
`;

const DisclaimerParagraph = styled.p`
  font-family: 'Fira Sans', sans-serif;
  color: #1b2457;
  font-weight: 400;
  font-style: normal;
  margin: 0;
  padding-bottom: 5px;
  font-size: 15px;
  line-height: 21px;
  letter-spacing: 0;

  & > a {
    color: #1b2457;
    text-decoration: underline;
  }

  @media (max-width: 390px) {
    font-size: 14px;
    line-height: 19px;
  }
`;

const DisclaimerHeading = styled.p`
  font-family: 'Fira Sans', sans-serif;
  color: #1b2457;
  font-weight: 700;
  font-style: normal;
  margin: 0 0 5px 0;
  padding: 0;
  font-size: 15px;
  line-height: 21px;
  letter-spacing: 0;

  @media (max-width: 390px) {
    font-size: 14px;
    line-height: 19px;
  }
`;

const PrivacyNotice = styled.p`
  position: relative;
  z-index: 1;
  width: calc(100% - 48px);
  max-width: 540px;
  margin: clamp(24px, 6dvh, 50px) auto 0;
  color: #6c6c6c;
  font-family: 'Fira Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 100%;
  letter-spacing: 0;
  text-align: center;

  @media (max-width: 390px) {
    width: calc(100% - 32px);
    margin-top: 16px;
    font-size: 12px;
    line-height: 120%;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    margin-top: 18px;
  }
`;

const PrivacyLink = styled.a`
  color: #2e7cf6;
  text-decoration: underline;
`;

const KindredPluginActivationAndroidAccessibility: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessibility_instructions_page } = ANALYTICS_PAGE_NAMES;
  usePageViewTracking(accessibility_instructions_page, 'Kindred');

  const renderContent = () => {
    return (
      <Container>
        <AccessibilityHeader>
          <AccessibilityTitle>
            <Trans
              i18nKey="KindredPluginActivation.androidAccessibility.enablePermissions"
              t={t}
              components={{ br: <br /> }}
            />
          </AccessibilityTitle>
          <DisclaimerContainer tabIndex={0}>
            <Trans
              i18nKey="KindredPluginActivation.androidAccessibility.disclaimer"
              t={t}
              components={{
                paragraph: <DisclaimerParagraph />,
                heading: <DisclaimerHeading />,
                email: (
                  <a href="mailto:contact@kindredteam.com">
                    contact@kindredteam.com
                  </a>
                ),
              }}
            />
          </DisclaimerContainer>
          <PrivacyNotice>
            <Trans
              i18nKey="KindredPluginActivation.landing.privacyNotice"
              t={t}
              components={{
                br: <br />,
                policy: (
                  <PrivacyLink
                    href={env.privacy_policy_url}
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </PrivacyNotice>
        </AccessibilityHeader>
        <BottomContent>
          <ActivateNow
            navigateToSettings={() => {
              navigate({
                pathname: Path.KindredPluginAndroidInstructions,
                search: window.location.search,
              });
            }}
            close={() => {
              onClose?.();
            }}
          />
        </BottomContent>
      </Container>
    );
  };

  return renderContent();
};

export default KindredPluginActivationAndroidAccessibility;
